import React from "react"
import Grid from '@material-ui/core/Grid'
import { makeStyles } from '@material-ui/core/styles'
import { Link } from "gatsby"

import SEO from "../components/seo"
import Layout from '../components/layout'
import QuizData from '../components/Page-Data/QuizData.json'
import '../CSS/WellnessTools.css'

const useStyles = makeStyles(() => ({
  root: { flexGrow: 1 },
}))

function calcScore(answers) {
  return QuizData.questions.reduce((total, q, i) => {
    const raw = answers[i] !== undefined ? answers[i] : 0
    const score = q.reversed ? (4 - raw) : raw
    return total + score
  }, 0)
}

function getResult(score) {
  const { low, moderate, high } = QuizData.results
  if (score <= 13) return { ...low,      tier: 'low' }
  if (score <= 26) return { ...moderate, tier: 'moderate' }
  return { ...high, tier: 'high' }
}

export default function WellnessQuiz() {
  const classes = useStyles()

  const [current, setCurrent]     = React.useState(0)   // current question index
  const [answers, setAnswers]     = React.useState({})   // { [qIndex]: value }
  const [showResult, setShowResult] = React.useState(false)
  const [score, setScore]         = React.useState(null)

  const total = QuizData.questions.length
  const question = QuizData.questions[current]
  const progressPct = Math.round(((current + (answers[current] !== undefined ? 1 : 0)) / total) * 100)

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [current]: value }))
  }

  const handleNext = () => {
    if (current < total - 1) {
      setCurrent(prev => prev + 1)
    } else {
      const finalScore = calcScore(answers)
      setScore(finalScore)
      setShowResult(true)
    }
  }

  const handlePrev = () => {
    if (current > 0) setCurrent(prev => prev - 1)
  }

  const handleRetake = () => {
    setCurrent(0)
    setAnswers({})
    setShowResult(false)
    setScore(null)
  }

  const result = showResult && score !== null ? getResult(score) : null

  return (
    <Layout>
      <SEO
        title="Stress & Wellness Quiz | Pratiiti Luminaries"
        description="Take Pratiiti's free Stress & Wellness Quiz to understand your current stress levels and get personalised tips. Based on the Perceived Stress Scale."
        canonical="https://www.pratiiti.in/wellness-quiz"
      />

      <div className={classes.root}>
        <Grid container justify="center">
          <Grid item xs={12} sm={10} md={8}>
            <div className="wellness-page-wrapper">

              <div className="pageheading">Stress & Wellness Quiz</div>
              <p className="wellness-section-subtitle">
                This 10-question quiz is based on the Perceived Stress Scale — a widely used tool in psychological research.
                It takes about 2 minutes. Your answers are completely private.
              </p>

              <div className="wellness-card">

                {showResult && result ? (
                  /* ── Result Screen ── */
                  <div className="quiz-result-card">
                    <div className={`quiz-result-badge ${result.badgeClass}`}>
                      {result.badge}
                    </div>
                    <div className="quiz-result-score">{score}</div>
                    <div className="quiz-result-score-label">out of 40</div>
                    <div className="quiz-result-title">{result.title}</div>
                    <p className="quiz-result-message">{result.message}</p>

                    <ul className="quiz-tips-list">
                      {result.tips.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>

                    <div className="wellness-btn-group">
                      <button className="wellness-btn" onClick={handleRetake}>
                        Retake Quiz
                      </button>
                      {result.showCTA && (
                        <Link to="/contact-us">
                          <button className="wellness-btn wellness-btn-accent">
                            {result.ctaText}
                          </button>
                        </Link>
                      )}
                    </div>

                    <div style={{ marginTop: 32 }}>
                      <p style={{ color: '#555', fontSize: 13, marginBottom: 12 }}>Try other wellness tools:</p>
                      <Link to="/breathing-guide">
                        <button className="wellness-btn" style={{ marginRight: 8 }}>Breathing Guide</button>
                      </Link>
                      <Link to="/guided-visualization">
                        <button className="wellness-btn">Guided Visualisation</button>
                      </Link>
                    </div>
                  </div>

                ) : (
                  /* ── Question Screen ── */
                  <>
                    {/* Progress bar */}
                    <div className="quiz-progress-wrapper">
                      <div className="quiz-progress-label">
                        Question {current + 1} of {total}
                      </div>
                      <div className="quiz-progress-bar-track">
                        <div
                          className="quiz-progress-bar-fill"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>

                    {/* Question */}
                    <div className="quiz-question-text">{question.text}</div>

                    {/* Options */}
                    <ul className="quiz-options-list">
                      {QuizData.options.map((opt) => (
                        <li
                          key={opt.value}
                          role="presentation"
                          className={`quiz-option-item${answers[current] === opt.value ? ' selected' : ''}`}
                          onClick={() => handleSelect(opt.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSelect(opt.value)}
                        >
                          <input
                            type="radio"
                            id={`opt-${opt.value}-q${current}`}
                            name={`q-${current}`}
                            value={opt.value}
                            checked={answers[current] === opt.value}
                            onChange={() => handleSelect(opt.value)}
                          />
                          <label htmlFor={`opt-${opt.value}-q${current}`}>{opt.label}</label>
                        </li>
                      ))}
                    </ul>

                    {/* Navigation */}
                    <div className="wellness-btn-group">
                      {current > 0 && (
                        <button className="wellness-btn" onClick={handlePrev}>
                          ← Back
                        </button>
                      )}
                      <button
                        className="wellness-btn wellness-btn-primary"
                        onClick={handleNext}
                        disabled={answers[current] === undefined}
                      >
                        {current < total - 1 ? 'Next →' : 'See My Results'}
                      </button>
                    </div>
                  </>
                )}

              </div>

              {/* Disclaimer */}
              <p style={{ fontSize: 12, color: '#888', textAlign: 'center', marginTop: 16, lineHeight: 1.6 }}>
                This quiz is for self-awareness purposes only and is not a clinical diagnosis.
                If you are in distress, please{' '}
                <Link to="/contact-us" style={{ color: '#f7941e' }}>reach out to a counsellor</Link>.
              </p>

            </div>
          </Grid>
        </Grid>
      </div>
    </Layout>
  )
}
